/**
 * 主檔 · 供應商面板（`ui-spec.md` 頁 15 上半）
 *
 * ⚠️ 只係 UI：**建立權限由後端** `guard(..., ['staff','admin'])` 把關
 *    （供應商打 POST `/api/suppliers` → 403，見 `test-master.mjs`）。
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '@coeldery/empty-state';
import FormTextField from '@coeldery/form-text-field';
import { btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type Supplier } from '../lib/api';

export interface SupplierAdminPanelProps {
  suppliers: Supplier[];
  onChanged: () => Promise<void>;
}

interface FormState {
  code: string;
  name: string;
  contactName: string;
  contactPhone: string;
  waNumber: string;
}

const BLANK: FormState = { code: '', name: '', contactName: '', contactPhone: '', waNumber: '' };

export default function SupplierAdminPanel({ suppliers, onChanged }: SupplierAdminPanelProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(BLANK);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const set = (key: keyof FormState) => (v: string | null) =>
    setForm((f) => ({ ...f, [key]: v ?? '' }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await api.createSupplier({
        code: form.code.trim(),
        name: form.name.trim(),
        contactName: form.contactName,
        contactPhone: form.contactPhone,
        waNumber: form.waNumber,
      });
      setForm(BLANK);
      setDone(true);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ ...card, padding: '16px' }}>
      <h2 style={{ margin: '0 0 10px', fontSize: '17px', fontWeight: 800 }}>
        {t('master.suppliersTitle')}
      </h2>

      {suppliers.length === 0 ? (
        <EmptyState titleKey="master.emptySuppliersTitle" bodyKey="master.emptySuppliersBody" />
      ) : (
        <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
          {suppliers.map((s) => (
            <li
              key={s.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'baseline',
                padding: '10px 0',
                borderBottom: '1px solid var(--color-divider)',
                opacity: s.active ? 1 : 0.5,
              }}
            >
              <strong style={{ fontSize: '15px' }}>{s.code}</strong>
              <span>{s.name}</span>
              {s.contactPhone && <span style={hintStyle}>{s.contactPhone}</span>}
              {s.waNumber && <span style={hintStyle}>WA {s.waNumber}</span>}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} style={{ display: 'grid', gap: '12px' }}>
        <FormTextField id="f-sup-code" labelKey="master.code" value={form.code} onChange={set('code')} />
        <FormTextField id="f-sup-name" labelKey="master.name" value={form.name} onChange={set('name')} />
        <FormTextField
          id="f-sup-contact"
          labelKey="master.contactName"
          value={form.contactName}
          onChange={set('contactName')}
        />
        <FormTextField
          id="f-sup-phone"
          labelKey="master.contactPhone"
          value={form.contactPhone}
          onChange={set('contactPhone')}
        />
        <FormTextField
          id="f-sup-wa"
          labelKey="master.waNumber"
          value={form.waNumber}
          onChange={set('waNumber')}
        />
        <div>
          <button type="submit" style={btnPrimary} disabled={busy}>
            {busy ? t('common.adding') : t('master.addSupplier')}
          </button>
        </div>
        {error && (
          <p style={errText} role="alert">
            {error}
          </p>
        )}
        {done && (
          <p style={{ ...hintStyle, color: 'var(--color-primary)', fontWeight: 700 }}>
            {t('master.supplierCreated')}
          </p>
        )}
      </form>
    </section>
  );
}
